import React, {
	ChangeEvent, ClipboardEvent, FC, KeyboardEvent, useEffect, useMemo,
} from 'react';

import {
	AsyncCellSearch,
	ICellEditor,
} from '@aleph/shared-components';
import {
	LocationOptionsQuery,
	RedditLocationTargetingLabelsQuery,
	RedditLocationTargetingLabelsQueryVariables,
} from 'wb-reddit/graphql/generated';
import redditLocationLabelsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/locationLabels.graphql';
import { useLazyQuery } from '@apollo/client';
import { uniqBy } from 'lodash';

const LocationsCellEditor: FC<ICellEditor<LocationOptionsQuery['options']['items']>> = (
	params,
) => {
	const initialValue = useMemo(() => params.value, []);
	const {
		data, value, setValue, editorContext, setEditorContext,
	} = params;

	const [getLocationLabels, { loading }] = useLazyQuery<RedditLocationTargetingLabelsQuery, RedditLocationTargetingLabelsQueryVariables>(redditLocationLabelsQuery);

	const changeSearch = (e: ChangeEvent<HTMLInputElement>) => {
		setEditorContext({
			...editorContext,
			search: e.target.value,
		});
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			params.stopEditing();
		}

		if (e.key === 'Escape') {
			setValue(initialValue);

			setTimeout(() => params.stopEditing());
		}
	};

	useEffect(() => {
		setEditorContext({
			...editorContext,
			adAccountId: data?.parent?.parent?.adAccount?.adAccountId,
		});
	}, [data]);

	const onPaste = async (e: ClipboardEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const locationIds = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		try {
			const { data: locationData } = await getLocationLabels({
				variables: {
					locationIds,
					locationNames: locationIds,
				},
			});

			setValue(
				uniqBy(
					[
						...(value ?? []),
						...(locationData?.targetingGeolocations?.items ?? []),
						...(locationData?.targetingGeolocationsByLabel?.items ?? []),
					] as LocationOptionsQuery['options']['items'],
					'value',
				),
			);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<AsyncCellSearch onChange={changeSearch}
			onKeyDown={onKeyDown}
			onPaste={onPaste}
			value={editorContext?.search ?? ''}
			loading={loading}
			loaderText='Pasting...'
			placeholder='Search locations...'
			onSubmit={() => params.stopEditing()}
			onCancel={() => params.api.stopEditing(true)}
		/>
	);
};

export default LocationsCellEditor;
